import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment/moment';
import { BarChart, LineChart, pieArcLabelClasses, PieChart } from '@mui/x-charts';
import {
    BarChartData,
    BarChartTwoLineData,
    ChartTypes,
    ChartViewEnums,
    ChartViewTypes,
    LineChartData,
    PieChartData,
    renderChartValueFormat,
    toDollar,
} from './constants';
import { FindLoad, Images } from 'utils';
import { Loader, SelectTypeAutocomplete, TextRow } from 'components';
import { analyticActions } from 'store';
import { DefaultDates, ValueFormatter, ValueTypes } from '../constants';
import LineCard from './line';

const ACTION_TYPE = 'GET_CHART_DATA';
export const ChartView = () => {
    const { chartData } = useSelector((state) => ({
        chartData: state.analytics.chartData,
    }));
    const dispatch = useDispatch();
    const load = FindLoad(ACTION_TYPE);
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const activeChart = info?.activeChart;
    const [chartType, setChartType] = useState();
    const [chartView, setChartView] = useState(ChartViewEnums.MONTHLY);
    const { allDates } = DefaultDates(info);
    // Bar Chart Data
    const { dates, current } = BarChartData(info, chartData?.currentPeriod, chartData?.prevPeriod || [], chartView);
    // Line Chart Data
    const { lineChartDates } = LineChartData(info, chartData?.currentPeriod, chartView);
    // Pie Chart Data
    const { pieChartData } = PieChartData(chartType, chartData);
    // Bar Two line Chart Data
    const firstLine = chartData && chartData?.currentPeriod?.filter((i) => i?.label === 'billedAmt');
    const secondLine = chartData && chartData?.currentPeriod?.filter((i) => i?.label === 'paidAmt');
    const { lineDates, firstLineData, secondLineData } = BarChartTwoLineData(info, firstLine || [], secondLine || [], chartView);
    const twoLineBarChart = activeChart?.name === 'Revenue';

    useEffect(() => {
        if (activeChart) {
            setChartType(activeChart?.chartTypes[0]);
            dispatch(analyticActions.getChartData(activeChart?.apiPath, allDates));
        }
    }, [activeChart, info]);

    const changeType = (type) => {
        setChartType(type);
    };

    function valueFormatter(value) {
        if (activeChart?.valueFormat === ValueTypes.DOLLAR) {
            return toDollar(value, 2);
        } else {
            return `${renderChartValueFormat(activeChart?.valueFormat)}${value}`;
        }
    }

    if (load?.length) {
        return <Loader />;
    }

    return (
        <div className="bar-chart-box">
            <div className="chart-title-action-box">
                <div className="flex-align-center" style={{ gap: 20 }}>
                    <p className="chart-title">{activeChart?.name}</p>
                    {/*{chartType !== ChartTypes.PIE &&*/}
                    {/*    <div className="flex-align-center" style={{ gap: 20 }}>*/}
                    {/*        /!*<div className="previous-current-box">*!/*/}
                    {/*        /!*    <p>Previous</p>*!/*/}
                    {/*        /!*    <div style={{ background: chartType === ChartTypes.LINE ? '#6FD231' : '#8FB9F2' }} />*!/*/}
                    {/*        /!*</div>*!/*/}
                    {/*        <div className="previous-current-box">*/}
                    {/*            <p>Current</p>*/}
                    {/*            <div style={{ background: '#0C66E4' }} />*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*}*/}
                </div>

                <div className="chart-types-box">
                    {chartType !== ChartTypes.PIE && (
                        <div className="select-chard-selector">
                            <SelectTypeAutocomplete
                                name={'tab'}
                                handleSelect={(e) => setChartView(e?.target?.value)}
                                defaultValue={chartView}
                                list={ChartViewTypes}
                                renderValue={(i) => i?.name}
                                uiType={'tableFilter'}
                                noError={true}
                                startIcon={Images.calendarToday}
                            />
                        </div>
                    )}

                    <div className="chart-type-buttons-box">
                        {activeChart?.chartTypes?.map((item, j) => (
                            <button
                                style={activeChart?.chartTypes?.length === 1 ? { borderRadius: '4px' } : {}}
                                key={j}
                                className={chartType === item ? 'active-chart' : 'passive-chart'}
                                onClick={() => changeType(item)}
                            >
                                <img
                                    src={
                                        item === ChartTypes.BAR
                                            ? Images.barChart
                                            : item === ChartTypes.LINE
                                              ? Images.lineChart
                                              : item === ChartTypes.PIE
                                                ? Images.pieChart
                                                : null
                                    }
                                    alt="icon"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {chartType === ChartTypes.BAR ? (
                <div style={{ padding: '0 20px' }}>
                    {twoLineBarChart ? (
                        <div className="two-line-chart-box">
                            <BarChart
                                borderRadius={7}
                                height={450}
                                grid={{ vertical: true, horizontal: true }}
                                yAxis={[{ valueFormatter: (value) => ValueFormatter(value, activeChart?.valueFormat) }]}
                                xAxis={[
                                    {
                                        scaleType: 'band',
                                        data: lineDates || [],
                                        valueFormatter: (date) =>
                                            chartView === ChartViewEnums.DAILY ? moment(date).format('MM/DD/YY') : date,
                                    },
                                ]}
                                series={[
                                    {
                                        label: 'Billed Amt.',
                                        valueFormatter,
                                        data: firstLineData || [],
                                        color: '#1976D2',
                                    },
                                    {
                                        label: 'Paid Amt.',
                                        valueFormatter,
                                        data: secondLineData || [],
                                        color: '#12B76A',
                                    },
                                ]}
                                slotProps={{
                                    legend: {
                                        direction: 'row',
                                        position: { vertical: 'top', horizontal: 'right' },
                                    },
                                }}
                            />
                        </div>
                    ) : (
                        <BarChart
                            borderRadius={7}
                            height={450}
                            grid={{ vertical: true, horizontal: true }}
                            yAxis={[{ valueFormatter: (value) => ValueFormatter(value, activeChart?.valueFormat) }]}
                            xAxis={[
                                {
                                    scaleType: 'band',
                                    data: dates || [],
                                    valueFormatter: (date) => (chartView === ChartViewEnums.DAILY ? moment(date).format('MM/DD/YY') : date),
                                },
                            ]}
                            series={[
                                // {
                                //     label: 'Previous',
                                //     data: prev || [],
                                //     color: '#90CAF9',
                                // },
                                {
                                    // label: renderChartValueFormat(activeChart?.valueFormat),
                                    valueFormatter,
                                    data: current || [],
                                    color: '#1976D2',
                                },
                            ]}
                        />
                    )}
                </div>
            ) : chartType === ChartTypes.LINE ? (
                <div className="line-chat">
                    <LineCard activeChart={activeChart} lineChartDates={lineChartDates} chartView={chartView} />
                </div>
            ) : chartType === ChartTypes.PIE ? (
                <div className="pie-chart-box">
                    <div style={{ width: 350 }}>
                        <PieChart
                            sx={{
                                [`& .${pieArcLabelClasses.root}`]: {
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                },
                            }}
                            series={[
                                {
                                    outerRadius: 150,
                                    data: pieChartData ? pieChartData : [],
                                    highlightScope: { fade: 'global', highlight: 'item' },
                                    arcLabel: (item) => `${item.value ? item.value : ''}`,
                                    faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                                    arcLabelMinAngle: 10,
                                },
                            ]}
                            slotProps={{
                                legend: { hidden: true },
                            }}
                            height={400}
                            width={400}
                        />
                    </div>
                    <div className="pie-chard-legend-wrapper">
                        {pieChartData?.map((i, j) => (
                            <div key={j} className="pie-chart-legend-box">
                                <div style={{ background: i.color }} />
                                <span style={{ fontWeight: 600 }}>{`(${i?.value})`}</span>
                                <TextRow name={i.label} />
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
